import { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';

import {
  useLoyaltyServiceWalmartEligibilityLazyQuery,
  useLoyaltyWalmartEligibilityLazyQuery,
} from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { WalmartEligibility, WalmartEligibilityInput, WalmartEligibilityResult } from '../types';

type WalmartEligibilitySource = 'legacy' | 'service';

interface WalmartEligibilityState extends WalmartEligibilityResult {
  readonly authz?: string;
  readonly called: boolean;
  readonly loading: boolean;
}

const INITIAL_STATE: WalmartEligibilityState = {
  called: false,
  loading: false,
};

const toApolloError = (error: unknown): ApolloError =>
  error instanceof ApolloError
    ? error
    : new ApolloError({
        errorMessage: error instanceof Error ? error.message : String(error),
      });

const normalizeEligibility = (
  eligibility: WalmartEligibility | null | undefined
): WalmartEligibility | undefined => eligibility ?? undefined;

export function useWalmartEligibility(activeAuthz?: string) {
  const useService = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_PARTNERS));
  const source: WalmartEligibilitySource = useService ? 'service' : 'legacy';
  const [executeLegacyQuery] = useLoyaltyWalmartEligibilityLazyQuery({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const [executeServiceQuery] = useLoyaltyServiceWalmartEligibilityLazyQuery({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const [state, setState] = useState<WalmartEligibilityState>(INITIAL_STATE);
  const requestGeneration = useRef(0);
  const activeAuthzRef = useRef(activeAuthz);
  activeAuthzRef.current = activeAuthz;

  useEffect(() => {
    requestGeneration.current += 1;
    setState(INITIAL_STATE);
    return () => {
      requestGeneration.current += 1;
    };
  }, [activeAuthz]);

  const getEligibility = useCallback(
    async ({ authz, loyaltyId }: WalmartEligibilityInput): Promise<WalmartEligibilityResult> => {
      const requestSource = source;
      const generation = requestGeneration.current + 1;
      requestGeneration.current = generation;

      const isCurrentRequest = () =>
        requestGeneration.current === generation && activeAuthzRef.current === authz;

      setState({
        authz,
        called: true,
        loading: true,
      });

      try {
        let data: WalmartEligibility | undefined;
        let resultError: ApolloError | undefined;

        if (requestSource === 'service') {
          const result = await executeServiceQuery({
            context: { queryDeduplication: false },
            variables: { input: { authz } },
          });
          data = normalizeEligibility(result.data?.loyaltyServiceWalmartEligibility);
          resultError = result.error;
        } else {
          const result = await executeLegacyQuery({
            context: { queryDeduplication: false },
            variables: { input: { authz, loyaltyId } },
          });
          data = normalizeEligibility(result.data?.loyaltyGetWalmartEligibility);
          resultError = result.error;
        }

        if (!isCurrentRequest()) {
          return {};
        }

        if (resultError) {
          setState({
            authz,
            called: true,
            error: resultError,
            loading: false,
          });
          return { error: resultError };
        }

        setState({
          authz,
          called: true,
          data,
          loading: false,
        });
        return { data };
      } catch (error) {
        if (!isCurrentRequest()) {
          return {};
        }

        const apolloError = toApolloError(error);
        setState({
          authz,
          called: true,
          error: apolloError,
          loading: false,
        });
        throw apolloError;
      }
    },
    [executeLegacyQuery, executeServiceQuery, source]
  );

  const stateMatchesSelection = state.authz === activeAuthz;

  return {
    called: stateMatchesSelection ? state.called : false,
    data: stateMatchesSelection ? state.data : undefined,
    error: stateMatchesSelection ? state.error : undefined,
    getEligibility,
    loading: stateMatchesSelection ? state.loading : false,
  };
}
